import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { profileCompleted } from "@/store/profile/profileActions";
import { progressBarSx, progressSx } from "../styles";

const TOTAL_ITEMS = 4;

const ProfileProgressBar = () => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const hasLoaded = useSelector((state: any) =>
    Boolean(state.profile.pageMetaData.get("profileCompletionLoaded"))
  );
  const isProfileComplete = useSelector((state: any) =>
    Boolean(state.profile.pageMetaData.get("isProfileComplete"))
  );
  const progressPercent = useSelector((state: any) => {
    const { summary, socialLinks, avatar, experience } = state.profile;
    let completed = 0;
    if (summary) completed += 1;
    if (avatar) completed += 1;
    if (experience.data.length) completed += 1;
    if (
      socialLinks.get("facebook") ||
      socialLinks.get("twitter") ||
      socialLinks.get("linkedin")
    )
      completed += 1;
    return (completed / TOTAL_ITEMS) * 100;
  });

  const isNowComplete = hasLoaded && !isProfileComplete && progressPercent === 100;

  /**
   * The class this replaced dispatched from inside render() by way of a
   * `handleProfileCompleted()` that returned null. Marking the profile
   * complete is a side effect, so it belongs in an effect.
   */
  useEffect(() => {
    if (isNowComplete) dispatch(profileCompleted());
  }, [isNowComplete, dispatch]);

  if (!hasLoaded || isProfileComplete || isNowComplete) return null;

  return (
    <Box component="section" sx={progressSx}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={progressBarSx}
        />
        <Typography
          variant="body2"
          sx={{ color: "grey.800", whiteSpace: "nowrap" }}
        >
          {`${progressPercent}% ${t("Completed")}`}
        </Typography>
      </Stack>
    </Box>
  );
};

export default ProfileProgressBar;
